import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { Response } from '../../../../shared/models/response.model';
import { Caractéristique } from '../models/caractéristique.model';
import { CaracteristiqueCreateRequest } from '../requests/caractéristique-create-request';
import { CaracteristiqueUpdateRequest } from '../requests/caractéristique-update-request';
import { CaractéristiqueService } from '../services/caractéristique.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { HttpErrorResponse } from '@angular/common/http';

export function avecMethodesCaractéristiques() {
  return signalStoreFeature(
    // Updater
    withMethods((store) => ({
      chargerCaracteristiques: (state: { caracteristiques: Caractéristique[] }) =>
        patchState(store, setAllEntities(state.caracteristiques)),
      ajouterCaracteristique: (state: { caracteristique: Caractéristique }) =>
        patchState(store, addEntity(state.caracteristique)),
      modifierCaracteristique: (state: { id: string; caracteristique: Partial<Caractéristique> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.caracteristique })),
      supprimerCaracteristique: (idCaracteristique: string) => patchState(store, removeEntity(idCaracteristique)),
    })),

    // Effects
    withMethods((store, caractéristiqueService = inject(CaractéristiqueService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idRéférentiel) =>
            caractéristiqueService.findAll(idRéférentiel).pipe(
              tapResponse({
                next: (result: Response<Caractéristique>) =>
                  store.chargerCaracteristiques({ caracteristiques: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        'Une erreur est survenue lors de la récupération des caractéristiques, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: CaracteristiqueCreateRequest; next: () => void; error: () => void }>(
        pipe(
          switchMap(({ data, next, error }) =>
            caractéristiqueService.create(data).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        'Une erreur est survenue lors de la création des caractéristiques, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });

                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idCaracteristique: string;
        data: CaracteristiqueUpdateRequest;
        next: () => void;
        error: () => void;
      }>(
        pipe(
          switchMap(({ idCaracteristique, data, next, error }) =>
            caractéristiqueService.update(idCaracteristique, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierCaracteristique({
                    id: idCaracteristique,
                    caracteristique: { ...data },
                  });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        'Une erreur est survenue lors de la modification de cette caractéristiques, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idCaracteristique: string; next: () => void }>(
        pipe(
          switchMap(({ idCaracteristique, next }) =>
            caractéristiqueService.supprimer(idCaracteristique).pipe(
              tapResponse({
                next: () => {
                  store.supprimerCaracteristique(idCaracteristique);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        'Une erreur est survenue lors de la suppression de cette caractéristiques, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
